import CompanyLogo from 'components/logos/CompanyLogo';
import { Button } from 'components/ui/button';
import useAddRecentDocument from 'domain/documents/hooks/useAddRecentDocument';
import useFavoriteToggle from 'domain/documents/hooks/useFavoriteToggle';
import { SearchDocument } from 'domain/documents/schemas/document';
import { Download, ExternalLink, Heart, HeartMinus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getProviderMeta } from 'types/provider';
import useDownload from 'utils/useDownload';

interface SearchSuggestionDropDownItemProps {
  suggestion: SearchDocument;
  index: number;
  canAnimateIn: boolean;
  onClose: () => void;
  onSelect: (suggestion: SearchDocument) => void;
}

export default function SearchSuggestionDropDownItem({
  suggestion,
  index,
  canAnimateIn,
  onClose,
  onSelect,
}: SearchSuggestionDropDownItemProps) {
  const { t } = useTranslation('searchPage');

  const { name: providerName } = getProviderMeta(suggestion.provider);

  const { download, isDownloading } = useDownload();
  const { mutate: addRecentDocument } = useAddRecentDocument();
  const { isFavorite, isPending: isMutatingFavorite, toggle: toggleFavorite } = useFavoriteToggle(suggestion);

  const hasFile = suggestion.downloadUrl !== '';
  // Un document qui pointe ailleurs s'ouvre dans une nouvelle page au lieu de se télécharger.
  const hasLink = suggestion.linkUrl !== '';

  const handleDownloadClick = () => {
    addRecentDocument(suggestion);
    download(suggestion.downloadUrl, suggestion.filename);
  };

  return (
    <div
      className={[
        'group flex items-center justify-between gap-3 rounded-xl px-3 py-2',
        'bg-white hover:bg-gray-100',
        'transition-all duration-200 ease-in-out',
        canAnimateIn ? 'translate-x-0 opacity-100' : 'translate-x-1 opacity-0',
      ].join(' ')}
      style={{ transitionDelay: `${index * 35}ms` }}
      role="option"
      tabIndex={0}
      onClick={() => {
        onSelect(suggestion);
        onClose();
      }}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          onSelect(suggestion);
          onClose();
        }
      }}
    >
      <div className="flex min-w-0 items-center gap-5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl">
          <CompanyLogo provider={suggestion.provider} isSearchPage={false} />
        </div>

        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-gray-900">{suggestion.title}</p>
          <p className="text-xs font-bold text-gray-500 italic">{providerName}</p>
          <p className="text-xs text-gray-500">{hasLink ? t('suggestion.link') : t('suggestion.document')}</p>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className={`${
            isFavorite ? 'border-red-300 bg-red-100' : ''
          } group/like h-9 w-9 rounded-full border transition hover:border-red-300 hover:bg-red-100 active:scale-95`}
          disabled={isMutatingFavorite}
          aria-label={isFavorite ? t('removeFavorite') : t('addFavorite')}
          onClick={e => {
            e.stopPropagation();
            toggleFavorite();
          }}
        >
          {!isFavorite ? (
            <Heart className="h-4 w-4 transition-colors ease-in-out group-hover/like:text-red-500" />
          ) : (
            <HeartMinus className="h-4 w-4 text-red-500 transition-colors ease-in-out" />
          )}
        </Button>
        {hasLink ? (
          <Button
            asChild
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full border transition hover:border-secondaryYellow hover:bg-secondaryYellow/50 active:scale-95"
          >
            <a
              href={suggestion.linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('suggestion.ariaLabelLink', { title: suggestion.title })}
              onClick={e => {
                e.stopPropagation();
                addRecentDocument(suggestion);
              }}
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full border transition hover:border-secondaryYellow hover:bg-secondaryYellow/50 active:scale-95"
            aria-label={t('suggestion.ariaLabelDownload', { title: suggestion.title })}
            onClick={e => {
              e.stopPropagation();
              handleDownloadClick();
            }}
            disabled={!hasFile || isDownloading}
          >
            <Download className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
